import {
	ComboData,
	FirebaseHook,
	FirebaseProjects,
	FirebaseQueryFn,
	FirebaseRemoteKeys,
	FirebaseRequestConfiguration,
	NumberKeyData,
} from '@interfaces/firebase.interfaces';
import { FirebaseOptions, getApp, getApps, initializeApp } from 'firebase/app';
import { getAll, getRemoteConfig, fetchAndActivate } from 'firebase/remote-config';
import Axios, { AxiosRequestConfig } from 'axios';
import useFirebaseStore from '@stores/firebase.store';
import useAuthStore from '@stores/auth.store';
import { Firestore, collection, getFirestore, query } from 'firebase/firestore';
import { getAuth, signInWithEmailAndPassword } from 'firebase/auth';
import useRequests from './requests.hook';
import { Combo, Cuenta, Terminal } from '@interfaces/requests.interfaces';

function useFirebase(): FirebaseHook {
	// Hooks
	const {
		requestCombos,
		requestMantenimientoOperativo,
		requestBackendSuite,
		requestNivelesDeServicio,
	} = useRequests();
	const store = useFirebaseStore();
	const authStore = useAuthStore();

	// Funciones internas
	function getConfig(project: FirebaseProjects): FirebaseOptions {
		return {
			apiKey: process.env[`REACT_APP_${project}_APIKEY`],
			authDomain: process.env[`REACT_APP_${project}_AUTHDOMAIN`],
			databaseURL: process.env[`REACT_APP_${project}_DB`],
			projectId: process.env[`REACT_APP_${project}_PID`],
			storageBucket: process.env[`REACT_APP_${project}_SB`],
			messagingSenderId: process.env[`REACT_APP_${project}_SID`],
			appId: process.env[`REACT_APP_${project}_APPID`],
			measurementId: process.env[`REACT_APP_${project}_MID`],
		};
	}

	// Funciones públicas
	async function initialize(): Promise<void> {
		const apps = getApps();

		const suite = apps.find((a) => a.name === FirebaseProjects.SUITE);
		if (!suite) {
			initializeApp(getConfig(FirebaseProjects.SUITE), FirebaseProjects.SUITE);
		}

		const apiClientes = apps.find((a) => a.name === FirebaseProjects.API_CLIENTES);
		if (!apiClientes) {
			initializeApp(getConfig(FirebaseProjects.API_CLIENTES), FirebaseProjects.API_CLIENTES);
		}

		const apiClientesAuth = getAuth(getApp(FirebaseProjects.API_CLIENTES));
		signInWithEmailAndPassword(apiClientesAuth, 'daniel.solarte@qcode.co', '123456');

		try {
			const accounts = await requestMantenimientoOperativo<Cuenta[]>('cuentas');
			if (accounts.isError) {
				throw new Error(accounts.message);
			}

			store.setAccounts(
				accounts.data.reduce((acc: NumberKeyData, cur) => {
					if (!acc[cur.codigo_cuenta]) {
						acc[cur.codigo_cuenta] = cur.nombre;
					}

					return acc;
				}, {})
			);
		} catch (error) {
			console.error('Consulta cuentas', error);
		}

		try {
			const combos = await requestCombos<Combo[]>();
			if (combos.isError) {
				throw new Error(combos.message);
			}

			store.setCombos(
				combos.data.reduce((acc: ComboData, cur) => {
					if (cur.estado && !acc[cur.id_combo]) {
						acc[cur.id_combo] = cur;
					}

					return acc;
				}, {})
			);
		} catch (error) {
			console.error('Consulta combos', error);
		}

		try {
			const serviceLevels = await requestNivelesDeServicio<any>();
			if (serviceLevels.isError) {
				throw new Error(serviceLevels.message);
			}

			const services = [
				...(serviceLevels.data.servicios ?? []),
				...(serviceLevels.data.complementarios ?? []),
			];

			let dataServices = {};
			services.map((cur: { codigo: number; nombre: string }) => {
				dataServices = { ...dataServices, [cur.codigo]: cur.nombre };
				return { [cur.codigo]: cur.nombre };
			}, {});
			store.setServiceLevels(dataServices);
		} catch (error) {
			console.error('Consulta niveles de servicio', error);
		}

		try {
			const terminals = await requestMantenimientoOperativo<Terminal[]>('terminales');
			if (terminals.isError) {
				throw new Error(terminals.message);
			}

			store.setTerminals(
				terminals.data.reduce((acc: NumberKeyData, cur) => {
					if (!acc[cur.codigo_terminal]) {
						acc[cur.codigo_terminal] = cur.nombre;
					}

					return acc;
				}, {})
			);
		} catch (error) {
			console.error('Consulta terminales', error);
		}
	}

	/**
	 * Get firestore database by project.
	 *
	 * @function
	 * @returns `Firestore | undefined`
	 */
	function getDatabase(project: FirebaseProjects): Firestore {
		if (project === FirebaseProjects.API_CLIENTES) {
			return getFirestore(getApp(FirebaseProjects.API_CLIENTES));
		}
		if (project === FirebaseProjects.MANTENIMIENTO_OP) {
			const con = initializeApp(getConfig(FirebaseProjects.MANTENIMIENTO_OP));
			return getFirestore(con);
		}
		return getFirestore(getApp(FirebaseProjects.SUITE));
	}

	async function getValue(key: FirebaseRemoteKeys): Promise<string | undefined> {
		let hostsData = store.hosts;
		if (!hostsData) {
			const remoteConfig = getRemoteConfig(getApp(FirebaseProjects.SUITE));

			remoteConfig.settings = {
				fetchTimeoutMillis: 60000,
				minimumFetchIntervalMillis: 60000,
			};

			await fetchAndActivate(remoteConfig);

			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			hostsData = JSON.parse((getAll(remoteConfig).autenticacion as any)._value).value;

			store.setHosts(hostsData);
		}

		if (!hostsData) {
			return undefined;
		}

		if (key !== 'admin-suite') {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			return (hostsData.autenticacion as any)[key];
		}

		return hostsData['admin-suite'];
	}

	const handleQuery: FirebaseQueryFn = (project, collectionName, ...queryConstraints) => {
		const db = getDatabase(project);
		if (db) {
			const ref = collection(db, collectionName);
			return query(ref, ...queryConstraints);
		}

		return undefined;
	};

	/**
	 * Carga los niveles de servicio desde el endpoint sifa/niveles-servicios.
	 * Esta función valida que el token esté disponible antes de hacer la petición.
	 *
	 * @async
	 * @function
	 * @returns `Promise<void>`
	 */
	async function loadServiceLevels(): Promise<void> {
		// Validar que el token esté disponible
		const token = authStore.token || localStorage.getItem('token');
		if (!token) {
			console.warn('Token no disponible, no se pueden cargar los niveles de servicio');
			return;
		}

		// Evitar cargar si ya hay datos cargados
		if (Object.keys(store.serviceLevels).length > 0) {
			console.log('Niveles de servicio ya están cargados');
			return;
		}

		try {
			const serviceLevels = await requestBackendSuite<any>(`sifa/niveles-servicios`, 'GET');
			if (serviceLevels.isError) {
				throw new Error(serviceLevels.message);
			}
			const services = [
				...(serviceLevels.data.servicios ?? []),
				...(serviceLevels.data.complementarios ?? []),
			];
			let dataServices = {};
			services.map((cur: { codigo: number; nombre: string }) => {
				dataServices = { ...dataServices, [cur.codigo]: cur.nombre };
				return { [cur.codigo]: cur.nombre };
			}, {});
			store.setServiceLevels(dataServices);
			console.log('Niveles de servicio cargados exitosamente');
		} catch (error) {
			console.error('Consulta niveles de servicio', error);
		}
	}

	/**
	 * Esta función realiza una petición mediante [Axios](https://www.npmjs.com/package/axios) hacia
	 * los enlaces configurados en el Remote Config.
	 *
	 * ## Configuraciones
	 * - `body` _Body | undefined_ (Opcional)
	 * - `headers` _{ Authorization: string | undefined } | undefined_ (Opcional)
	 * - `host` _'admin-suite' | 'host' | 'permisos' | 'logout'_
	 * - `method` _'DELETE' | 'GET' | 'PATCH' | 'POST' | 'PUT'_ (Opcional - Por defecto `GET`)
	 * - `responseType` _'arraybuffer' | 'blob' | 'document' | 'json' | 'text' | 'stream'_ (Opcional)
	 * - `url` _string_
	 *
	 * @async
	 * @function
	 * @param settings `FirebaseRequestConfiguration<Body>`
	 * @returns `Promise<Body>`
	 */
	async function request<Body>(settings: FirebaseRequestConfiguration<Body>): Promise<Body> {
		const host = (settings.host ? await getValue(settings.host) : undefined) ?? '';

		const fetch: AxiosRequestConfig = {
			url: host + settings.url,
			headers: settings.headers ?? {},
			method: settings.method ?? 'GET',
			data: settings.body,
		};

		if (settings.responseType) {
			fetch.responseType = settings.responseType;
		}

		const response = await Axios(fetch);
		return response.data;
	}

	return {
		accounts: store.accounts,
		combos: store.combos,
		getDatabase,
		getValue,
		handleQuery,
		initialize,
		loadServiceLevels,
		request,
		serviceLevels: store.serviceLevels,
		terminals: store.terminals,
	};
}

export default useFirebase;
